/**
 * Owner: Masa Bayu
 * File: raster.ts
 * Date:Time: 2026-09-30 15:25
 * Description: Decoded raster samples and soft mask carried on an IR image block.
 */

import { isRecord } from './guards';

export interface QXDocumentRasterMask {
  readonly samples: string;
  readonly width: number;
  readonly height: number;
  readonly colorSpace: string;
  readonly bitDepth: number;
  readonly matte: readonly number[];
}

export interface QXDocumentRaster {
  readonly samples: string;
  readonly width: number;
  readonly height: number;
  readonly colorSpace: string;
  readonly bitDepth: number;
  readonly mask: QXDocumentRasterMask | null;
}

function text(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`${label} must be a non-empty string`);
  }
  return value.trim();
}

function positive(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
    throw new Error(`${label} must be a positive integer`);
  }
  return value;
}

function matte(value: unknown): readonly number[] {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'number' || !Number.isFinite(item))) {
    throw new Error('raster mask matte must be finite numbers');
  }
  return Object.freeze([...value]);
}

function maskOf(value: unknown): QXDocumentRasterMask {
  if (!isRecord(value)) throw new Error('raster mask must be an object');
  return Object.freeze({
    samples: text(value.samples, 'raster mask samples'),
    width: positive(value.width, 'raster mask width'),
    height: positive(value.height, 'raster mask height'),
    colorSpace: text(value.colorSpace, 'raster mask colorSpace'),
    bitDepth: positive(value.bitDepth, 'raster mask bitDepth'),
    matte: matte(value.matte),
  });
}

export function createQXDocumentRaster(options: QXDocumentRaster): QXDocumentRaster {
  if (!isRecord(options)) throw new Error('raster must be an object');
  return Object.freeze({
    samples: text(options.samples, 'raster samples'),
    width: positive(options.width, 'raster width'),
    height: positive(options.height, 'raster height'),
    colorSpace: text(options.colorSpace, 'raster colorSpace'),
    bitDepth: positive(options.bitDepth, 'raster bitDepth'),
    mask: options.mask === null ? null : maskOf(options.mask),
  });
}
